import { useCallback, useEffect, useRef } from "react";

import { Map as MaplibreMap } from "maplibre-gl";

import {
  USER_LOCATION_ACCURACY_SOURCE_ID,
  USER_LOCATION_BOUNDS_PADDING,
  USER_LOCATION_COLOR,
  USER_LOCATION_MAX_ZOOM,
  USER_LOCATION_SOURCE_ID
} from "@/constants/map";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import {
  EGeolocationStatus,
  GeolocationFix,
  useGeolocation
} from "@/hooks/useGeolocation";
import { BoundingBox } from "@/types/map";
import {
  ensureMapStyleReady,
  getRadiusPolygon,
  removeAllSourceLayerWithPrefix
} from "@/utils/map";

import { bbox } from "@turf/turf";

import { TO_BOUNDS_DURATION } from "../interactions/useMapInteractions";

type UseUserLocationProps = {
  map: MaplibreMap | null;
  isMapLoaded: boolean;
};

/**
 * A cross-origin embed is refused geolocation unless the embedding page opts in
 * with `allow="geolocation"`, and that refusal arrives as the very same
 * PERMISSION_DENIED code as the user's own. No browser setting can lift it, so
 * when we are framed the copy has to name the embedder as a possible cause
 * instead of sending the user somewhere that cannot help them.
 */
const isEmbedded = () => window.self !== window.top;

/**
 * A denied permission is sticky: the app can never re-prompt, so the copy has
 * to point at whatever can actually lift it.
 */
const ERROR_MESSAGE: Partial<Record<EGeolocationStatus, string>> = {
  [EGeolocationStatus.DENIED]:
    "Location access is blocked. Allow location for this site in your browser settings, then try again.",
  [EGeolocationStatus.TIMEOUT]:
    "Finding your location took too long. Please try again.",
  [EGeolocationStatus.UNAVAILABLE]:
    "Your location is unavailable right now. Please try again.",
  [EGeolocationStatus.UNSUPPORTED]:
    "This browser can't share your location on a non-secure connection."
};

const DENIED_IN_EMBED_MESSAGE =
  "Location access is blocked — either by the page this app is embedded in, or in your browser settings.";

const errorMessageFor = (status: EGeolocationStatus) => {
  if (status === EGeolocationStatus.DENIED && isEmbedded()) {
    return DENIED_IN_EMBED_MESSAGE;
  }

  return (
    ERROR_MESSAGE[status] ?? ERROR_MESSAGE[EGeolocationStatus.UNAVAILABLE]!
  );
};

/** Removes the dot and the accuracy circle (they share a source-id prefix). */
const clearUserLocationDisplay = (map: MaplibreMap) => {
  removeAllSourceLayerWithPrefix(map, USER_LOCATION_SOURCE_ID, []);
};

/** Draws the accuracy circle first, then the dot, so the dot stays on top. */
const drawUserLocation = (map: MaplibreMap, fix: GeolocationFix) => {
  const center: [number, number] = [fix.longitude, fix.latitude];

  map.addSource(USER_LOCATION_ACCURACY_SOURCE_ID, {
    type: "geojson",
    data: getRadiusPolygon(center, fix.accuracy)
  });
  map.addLayer({
    id: `${USER_LOCATION_ACCURACY_SOURCE_ID}-fill`,
    type: "fill",
    source: USER_LOCATION_ACCURACY_SOURCE_ID,
    paint: {
      "fill-color": USER_LOCATION_COLOR,
      "fill-opacity": 0.12
    }
  });
  map.addLayer({
    id: `${USER_LOCATION_ACCURACY_SOURCE_ID}-outline`,
    type: "line",
    source: USER_LOCATION_ACCURACY_SOURCE_ID,
    paint: {
      "line-color": USER_LOCATION_COLOR,
      "line-width": 1,
      "line-opacity": 0.4
    }
  });

  map.addSource(USER_LOCATION_SOURCE_ID, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: { type: "Point", coordinates: center },
      properties: {}
    }
  });
  map.addLayer({
    id: `${USER_LOCATION_SOURCE_ID}-dot`,
    type: "circle",
    source: USER_LOCATION_SOURCE_ID,
    paint: {
      "circle-radius": 6,
      "circle-color": USER_LOCATION_COLOR,
      "circle-stroke-width": 3,
      "circle-stroke-color": "#FFFFFF"
    }
  });
};

/**
 * Frames the fix by its own accuracy rather than a fixed zoom, so a 3 km-wide
 * fix does not present itself at street level. `maxZoom` keeps a pinpoint fix
 * from zooming all the way in.
 */
const flyToUserLocation = (map: MaplibreMap, fix: GeolocationFix) => {
  const accuracyCircle = getRadiusPolygon(
    [fix.longitude, fix.latitude],
    fix.accuracy
  );

  map.fitBounds(bbox(accuracyCircle) as BoundingBox, {
    padding: USER_LOCATION_BOUNDS_PADDING,
    maxZoom: USER_LOCATION_MAX_ZOOM,
    duration: TO_BOUNDS_DURATION
  });
};

/**
 * Centers the map on the user's GPS position and marks it with a dot plus its
 * accuracy circle.
 *
 * Deliberately user-triggered: the map is built synchronously at the default
 * center, and only moves once a fix actually arrives — so nothing about first
 * paint waits on a permission dialog.
 */
export const useUserLocation = ({ map, isMapLoaded }: UseUserLocationProps) => {
  const { requestLocation, isLocating, isBlocked, isSupported } =
    useGeolocation();
  const lastFixRef = useRef<GeolocationFix | null>(null);

  const locateUser = useCallback(async () => {
    if (!map || !isMapLoaded) return;

    const { status, fix } = await requestLocation();

    if (!fix) {
      showToast({
        message: errorMessageFor(status),
        type: EToastType.ERROR
      });
      return;
    }

    lastFixRef.current = fix;

    // The geolocation await above can straddle a route change that swaps the map
    // instance. Clear inside the callback: a replay must not double-add.
    const redrawUserLocation = () => {
      clearUserLocationDisplay(map);
      drawUserLocation(map, fix);
    };

    if (!ensureMapStyleReady(map, redrawUserLocation)) return;

    redrawUserLocation();
    flyToUserLocation(map, fix);
  }, [map, isMapLoaded, requestLocation]);

  const clearUserLocation = useCallback(() => {
    if (!map) return;

    lastFixRef.current = null;
    clearUserLocationDisplay(map);
  }, [map]);

  // `setStyle` (base-map switching) drops every custom source, so the dot has
  // to be put back once the new style has settled.
  useEffect(() => {
    if (!map || !isMapLoaded) return;

    const onStyleData = () => {
      const fix = lastFixRef.current;
      if (!fix || map.getSource(USER_LOCATION_SOURCE_ID)) return;

      drawUserLocation(map, fix);
    };

    map.on("styledata", onStyleData);

    return () => {
      map.off("styledata", onStyleData);
    };
  }, [map, isMapLoaded]);

  return {
    locateUser,
    clearUserLocation,
    isLocatingUser: isLocating,
    isUserLocationBlocked: isBlocked,
    isUserLocationSupported: isSupported
  };
};

export default useUserLocation;
